/*
 * BreadcrumbsDropdown
 * @module Breadcrumbs
 * @flow
 */

import React from 'react';
import styled, { css } from 'styled-components/macro';
import { palette } from 'styled-theme';
import { ifProp } from 'styled-tools';
import { FormattedMessage } from 'react-intl';

import { Block } from '~/components/Block';
import { ClickAwayListener } from '~/components/ClickAwayListener';
// $FlowIssue
import { ReactComponent as IconDropdown } from '@/icons/ic-dropdown.svg';

import messages from './messages';

export type BreadcrumbsDropdownProps = {
  children?: React$Node,
  onToggle?: () => void,
  isVisible?: boolean,
  palette?: string,
};

const BreadcrumbsDropdownWrapper = styled.div`
  position: relative;
  margin-bottom: 5px;
`;

const BreadcrumbsDropdownPlaceholder = styled(Block)`
  padding: 5px 0 5px 11px;
  background-color: #f7fbfe;
  align-items: center;
  font-size: 14px;
  font-weight: 600;
  color: ${palette(1)};
  user-select: none;
  cursor: pointer;

  span {
    display: inline-flex;
    margin-right: 6px;
  }

  svg {
    margin-right: 12px;
    transition: transform 0.3s;
    ${ifProp(
      'isVisible',
      css`
        transform: rotate(180deg);
      `,
    )};
  }
`;

const BreadcrumbsDropdownContainer = styled(Block)`
  position: absolute;
  left: 0;
  top: calc(100% + 8px);
  display: block;
  padding: 16px 17px;
  max-height: 270px;
  background-color: #f7fbfe;
  overflow: auto;
  z-index: 1;

  .react-breadcrumbs-item {
    font-size: 14px;
  }
`;

/**
 * BreadcrumbsDropdown React-component
 * @memberof Breadcrumbs
 * @param {Object} props - properties
 * @param {React.Children} [props.children] - childs
 * @param {boolean} [props.isVisible] - show state of BreadcrumbsDropdown list
 * @param {Function} [props.onToggle = () => {}] - on toggle BreadcrumbsDropdown callback
 * @param {string} [props.palette = 'primary'] - current palette
 * @example <caption>Simple usage</caption>
 * <BreadcrumbsDropdown><div /></BreadcrumbsDropdown>
 * @returns {React.Node<*>} React-component
 */
export const BreadcrumbsDropdown = ({
  palette,
  isVisible,
  children,
  onToggle,
}: BreadcrumbsDropdownProps) => (
  <BreadcrumbsDropdownWrapper>
    <BreadcrumbsDropdownPlaceholder
      onClick={onToggle}
      palette={palette}
      isVisible={isVisible}
    >
      <FormattedMessage
        {...messages.more}
        values={{ count: Math.ceil(React.Children.count(children) / 2) }}
        tagName="span"
      />
      <IconDropdown />
    </BreadcrumbsDropdownPlaceholder>
    {isVisible && (
      <ClickAwayListener onClickAway={onToggle}>
        <BreadcrumbsDropdownContainer>{children}</BreadcrumbsDropdownContainer>
      </ClickAwayListener>
    )}
  </BreadcrumbsDropdownWrapper>
);

BreadcrumbsDropdown.defaultProps = {
  palette: 'primary',
  onToggle: () => {},
};
